/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import PropTypes from 'prop-types';
import type { LegacyContextType } from 'types/legacy-context-types';

import { Button, Grid, typography2 } from '@coursera/cds-core';

import SearchBar from 'bundles/discussions/components/search/SearchBar';
import TrackedTextInput from 'bundles/page/components/TrackedTextInput';

import _t from 'i18n!nls/discussions';

const styles = {
  searchBoxContainer: css`
    position: relative;
  `,
  inputArea: () => css`
    input {
      ${typography2.bodyPrimary};
      border-radius: 4px 0 0 4px;
      max-height: 36px;
      border: 1px solid var(--cds-color-grey-600);
      padding: var(--cds-spacing-100) var(--cds-spacing-150);
      width: 200px !important;
      border-right: 0;

      &::placeholder {
        color: var(--cds-color-neutral-primary-weak);
        opacity: 1;
      }
    }

    button {
      border-radius: 0 4px 4px 0;
    }
  `,
};

type Props = {
  animateToSearch: () => void;
  animateFromSearch: () => void;
  nextElementId?: string;
  previousElementId?: string;
};

type State = {
  showBar: boolean;
  query: string;
};

class SearchBox extends React.Component<Props, State> {
  static contextTypes = {
    router: PropTypes.object.isRequired,
  };

  declare context: LegacyContextType<typeof SearchBox.contextTypes>;

  state = {
    showBar: false,
    query: '',
  };

  handleQueryChange = (newQuery: string) => {
    this.setState({
      query: newQuery,
    });
  };

  handleFocus = () => {
    const { animateToSearch } = this.props;
    this.setState({
      showBar: true,
    });
    animateToSearch();
  };

  handleBlur = () => {
    const { animateFromSearch } = this.props;
    this.setState({
      showBar: false,
    });

    animateFromSearch();
  };

  handleClear = () => {
    this.setState({
      query: '',
    });
  };

  handleTab = (e: React.KeyboardEvent<HTMLElement>) => {
    const { nextElementId } = this.props;
    const element = nextElementId && document && document.getElementById(nextElementId);
    if (element) {
      e.preventDefault();
      element.focus();
    }
  };

  handleShiftTab = (e: React.KeyboardEvent<HTMLElement>) => {
    const { previousElementId } = this.props;
    const element = previousElementId && document && document.getElementById(previousElementId);
    if (element) {
      e.preventDefault();
      element.focus();
    }
  };

  submitSearch = () => {
    const { query } = this.state;
    const { router } = this.context;
    const {
      location: { pathname },
    } = router;
    if (query) {
      const q = query.replace('*', '');
      router.push({
        pathname,
        params: {},
        query: { q },
      });
    }
  };

  render() {
    const { showBar, query } = this.state;
    if (showBar) {
      return (
        <SearchBar
          query={query}
          handleSubmit={this.submitSearch}
          handleChange={this.handleQueryChange}
          handleBlur={this.handleBlur}
          handleShiftTab={this.handleShiftTab}
          handleTab={this.handleTab}
          handleClear={this.handleClear}
          focus
        />
      );
    } else {
      return (
        <div className="rc-SearchBox expand-block" css={styles.searchBoxContainer}>
          <div className="large-screen">
            <Grid container direction="row" wrap="nowrap" className="input-area" css={styles.inputArea}>
              <TrackedTextInput
                trackingName="search_box"
                onClick={this.handleFocus}
                // @ts-expect-error ts-migrate(2769) FIXME: No overload matches this call.
                onFocus={this.handleFocus}
                placeholder={_t('Search')}
                value={query}
                className="search-box search-input"
                aria-label={_t('Search')}
              />
              <Button
                type="submit"
                size="small"
                onClick={this.handleFocus}
                className="search-button"
                aria-label={_t('expand search box')}
              >
                {_t('Search')}
              </Button>
            </Grid>
          </div>
          <div className="mobile hide horizontal-box align-items-right">
            <Button
              type="button"
              size="small"
              variant="ghost"
              className="nostyle search-button mobile hide"
              onClick={this.handleFocus}
              aria-label={_t('Expand Search Box')}
            >
              <i className="cif-search" />
            </Button>
          </div>
        </div>
      );
    }
  }
}

export default SearchBox;
